import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function Login() {
  const { login, error, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) nav("/");
  };

  return (
    <div className="min-h-screen flex bg-slate-900">
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-14 text-slate-100 bg-[linear-gradient(140deg,#0f172a_0%,#1e293b_100%)]">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">Shady Acres Caravan Park</div>
          <div className="mt-2 font-heading text-3xl font-bold">Maintenance &amp; Compliance Manager</div>
        </div>
        <div className="max-w-md">
          <div className="text-xl font-heading font-medium leading-snug">
            One operational cockpit for scheduled maintenance, regulatory obligations, inspections, defects, work orders and contractor evidence.
          </div>
          <div className="mt-6 text-sm text-slate-400 space-y-1">
            <div>· Every dashboard tile drills into a filtered list</div>
            <div>· Australian Standards preserved, marked <span className="text-amber-300">verification required</span></div>
            <div>· Full audit trail — no silent deletion of compliance records</div>
          </div>
        </div>
        <div className="text-xs text-slate-500">Victoria, Australia · v1</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <Card className="w-full max-w-md border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading text-2xl">Sign in</CardTitle>
            <CardDescription>Enter your credentials to access the maintenance system.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email" type="email" required autoFocus autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password" type="password" required autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                />
              </div>
              {error && (
                <Alert variant="destructive" data-testid="login-error">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit">
                {busy ? "Signing in…" : "Sign in"}
              </Button>
            </form>
            <div className="mt-6 text-xs text-slate-500 leading-relaxed border-t border-slate-100 pt-4">
              <div className="text-slate-500">Shady Acres Caravan Park internal system.</div>
              <div>Forgot password? Contact the park owner.</div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
